import React, { useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
  Sparkle, BookOpen, CardsThree, ArrowRight, ArrowLeft, FloppyDisk,
  Trash, Plus, UploadSimple, Target, Lightbulb, FastForward, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AIProgressIndicator from "../components/AIProgressIndicator";

const CHUNK_SIZES = [
  { key: "short", label: "Short", desc: "1 sentence · tight bites" },
  { key: "medium", label: "Medium", desc: "2–3 sentences · default" },
  { key: "long", label: "Long", desc: "Full paragraph · for flow" },
];

export default function NewDeck() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "qa" ? "qa" : "script";
  const [mode, setMode] = useState(initialMode);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [chunkSize, setChunkSize] = useState("medium");

  // Practice settings
  const [matchMode, setMatchMode] = useState("exact");
  const [threshold, setThreshold] = useState(75);
  const [autoAdvance, setAutoAdvance] = useState(true);

  // Script mode
  const [sourceText, setSourceText] = useState("");
  const [chunks, setChunks] = useState([]);
  const [chunking, setChunking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  // Q&A mode
  const [qaStyle, setQaStyle] = useState("statement"); // "statement" | "flashcard"
  const [qaSource, setQaSource] = useState("paste"); // "paste" | "generate"
  const [qaInput, setQaInput] = useState("");
  const [qaItems, setQaItems] = useState([]); // {original?, statement?, prompt?, answer?}
  const [converting, setConverting] = useState(false);
  // Generate from topic
  const [topic, setTopic] = useState("");
  const [genCount, setGenCount] = useState(10);
  const [generating, setGenerating] = useState(false);

  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const canSave = useMemo(() => {
    if (!title.trim()) return false;
    if (mode === "script") return chunks.length > 0;
    if (qaStyle === "flashcard") return qaItems.some((x) => (x.prompt || "").trim() && (x.answer || "").trim());
    return qaItems.some((x) => (x.statement || "").trim());
  }, [title, mode, chunks, qaItems, qaStyle]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSourceText((prev) => (prev ? prev + "\n\n" : "") + data.text);
      toast.success(`Imported ${data.chars.toLocaleString()} characters from ${data.filename}`);
      if (!title.trim()) setTitle(data.filename.replace(/\.[^.]+$/, ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extract text");
    } finally { setUploading(false); }
  };

  const doChunk = async () => {
    if (!sourceText.trim()) { toast.error("Paste or upload some text first"); return; }
    setChunking(true);
    try {
      const { data } = await api.post("/ai/chunk-text", { text: sourceText, chunk_size: chunkSize });
      setChunks(data.chunks || []);
      if (!data.chunks?.length) toast.error("No chunks returned");
      else toast.success(`Split into ${data.chunks.length} chunks`);
    } catch {
      toast.error("Chunking failed");
    } finally { setChunking(false); }
  };

  const doConvertQA = async () => {
    if (!qaInput.trim()) { toast.error("Paste some questions first"); return; }
    setConverting(true);
    try {
      if (qaStyle === "flashcard") {
        const { data } = await api.post("/ai/convert-mcq-flashcard-bulk", { text: qaInput });
        setQaItems(data.items || []);
        toast.success(`Converted ${data.items?.length || 0} flashcard(s)`);
      } else {
        const { data } = await api.post("/ai/convert-mcq-bulk", { text: qaInput });
        setQaItems(data.items || []);
        toast.success(`Converted ${data.items?.length || 0} question(s)`);
      }
    } catch {
      toast.error("Conversion failed");
    } finally { setConverting(false); }
  };

  const doGenerate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic or notes"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-qa", { topic, count: genCount });
      setQaStyle("flashcard"); // generated items are prompt+answer
      setQaItems(data.items || []);
      toast.success(`Generated ${data.items?.length || 0} flashcard(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const updateChunk = (i, v) => setChunks((c) => c.map((x, idx) => idx === i ? v : x));
  const removeChunk = (i) => setChunks((c) => c.filter((_, idx) => idx !== i));
  const addChunk = () => setChunks((c) => [...c, ""]);
  const updateQA = (i, keyOrValue, value) => {
    // Backwards-compatible: updateQA(i, "statement", value) or updateQA(i, value)
    const key = value === undefined ? "statement" : keyOrValue;
    const v = value === undefined ? keyOrValue : value;
    setQaItems((arr) => arr.map((x, idx) => idx === i ? { ...x, [key]: v } : x));
  };
  const removeQA = (i) => setQaItems((arr) => arr.filter((_, idx) => idx !== i));
  const addQA = () => setQaItems((arr) => [
    ...arr,
    qaStyle === "flashcard"
      ? { original: "", prompt: "", answer: "" }
      : { original: "", statement: "" },
  ]);

  const save = async () => {
    setSaving(true);
    try {
      const deckPayload = {
        title: title.trim(),
        description: description.trim(),
        mode,
        qa_style: mode === "qa" ? qaStyle : "statement",
        chunk_size: chunkSize,
        source_text: mode === "script" ? sourceText : "",
        match_mode: matchMode,
        match_threshold: threshold,
        auto_advance: autoAdvance,
      };
      const { data: deck } = await api.post("/decks", deckPayload);

      let cards;
      if (mode === "script") {
        cards = chunks.filter((c) => c.trim()).map((c) => ({ content: c.trim() }));
      } else if (qaStyle === "flashcard") {
        cards = qaItems
          .filter((q) => (q.prompt || "").trim() && (q.answer || "").trim())
          .map((q) => ({
            prompt: q.prompt.trim(),
            content: q.answer.trim(),
            alt_answers: (q.alt_answers_text || "")
              .split("|")
              .map((s) => s.trim())
              .filter(Boolean),
            original: q.original,
          }));
      } else {
        cards = qaItems
          .filter((q) => (q.statement || "").trim())
          .map((q) => ({ content: q.statement.trim(), original: q.original }));
      }

      if (cards.length > 0) {
        await api.post(`/decks/${deck.deck_id}/cards`, cards);
      }
      toast.success("Deck saved");
      navigate(`/decks/${deck.deck_id}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not save deck");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12 fade-in">
        <Link to="/dashboard" className="text-sm link-underline inline-flex items-center gap-1 mb-8" data-testid="back-link">
          <ArrowLeft size={14} /> Back to library
        </Link>
        <div className="eyebrow mb-4">New deck</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">Compose a deck</h1>

        {/* Mode switch */}
        <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[color:var(--rule)] border rule" data-testid="mode-switch">
          {[
            { k: "script", icon: <BookOpen size={22} weight="duotone"/>, t: "Script or Text", d: "Rehearse a speech, study guide, or notes. Import PDF / Word." },
            { k: "qa", icon: <CardsThree size={22} weight="duotone"/>, t: "Q & A → Statements", d: "Paste MCQs; AI builds answer statements." },
          ].map((m) => (
            <button
              key={m.k}
              onClick={() => setMode(m.k)}
              className={`text-left p-8 transition-colors relative ${mode === m.k ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"}`}
              data-testid={`mode-${m.k}`}
            >
              {mode === m.k && <CheckCircle size={20} weight="fill" className="absolute top-4 right-4"/>}
              <div className={`flex items-center gap-3 ${mode === m.k ? "text-white" : "text-[color:var(--ink-muted)]"}`}>
                {m.icon}
                <span className="eyebrow">{mode === m.k ? "Selected" : "Choose"}</span>
              </div>
              <div className="font-serif text-2xl mt-4">{m.t}</div>
              <div className={`text-sm mt-2 ${mode === m.k ? "text-white/70" : "text-[color:var(--ink-muted)]"}`}>{m.d}</div>
            </button>
          ))}
        </div>

        {/* Meta */}
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow block mb-2">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q4 Board Deck — opening"
              className="w-full bg-white border rule px-4 py-3 text-lg focus:outline-none focus:border-[color:var(--ink)]"
              data-testid="input-title"
            />
          </div>
          <div>
            <label className="eyebrow block mb-2">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this for?"
              className="w-full bg-white border rule px-4 py-3 text-lg focus:outline-none focus:border-[color:var(--ink)]"
              data-testid="input-description"
            />
          </div>
        </div>

        {/* Practice settings */}
        <div className="mt-10 border rule bg-white p-6 md:p-8">
          <div className="eyebrow mb-4">Practice settings</div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="text-sm font-medium mb-3">Match mode</div>
              <div className="grid grid-cols-2 gap-px bg-[color:var(--rule)] border rule" data-testid="match-mode-switch">
                <button
                  onClick={() => setMatchMode("exact")}
                  className={`p-4 text-left relative transition-colors ${matchMode === "exact" ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"}`}
                  data-testid="match-mode-exact"
                >
                  {matchMode === "exact" && <CheckCircle size={16} weight="fill" className="absolute top-2 right-2"/>}
                  <Target size={18} weight="duotone"/>
                  <div className="font-mono text-xs uppercase tracking-widest mt-2">Exact</div>
                  <div className={`text-xs mt-1 ${matchMode === "exact" ? "text-white/70" : "text-[color:var(--ink-muted)]"}`}>Word overlap — strict for figures &amp; terms.</div>
                </button>
                <button
                  onClick={() => setMatchMode("concept")}
                  className={`p-4 text-left relative transition-colors ${matchMode === "concept" ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"}`}
                  data-testid="match-mode-concept"
                >
                  {matchMode === "concept" && <CheckCircle size={16} weight="fill" className="absolute top-2 right-2"/>}
                  <Lightbulb size={18} weight="duotone"/>
                  <div className="font-mono text-xs uppercase tracking-widest mt-2">Concept</div>
                  <div className={`text-xs mt-1 ${matchMode === "concept" ? "text-white/70" : "text-[color:var(--ink-muted)]"}`}>AI judges meaning — rephrase freely.</div>
                </button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">Pass threshold</span>
                <span className="font-mono text-sm" data-testid="threshold-value">{threshold}%</span>
              </div>
              <input
                type="range"
                min="40"
                max="100"
                step="5"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="w-full accent-[color:var(--ink)]"
                data-testid="threshold-slider"
              />
              <div className="flex justify-between font-mono text-[10px] text-[color:var(--ink-muted)] mt-1">
                <span>LENIENT 40%</span><span>STRICT 100%</span>
              </div>
              <label className="flex items-center gap-3 mt-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoAdvance}
                  onChange={(e) => setAutoAdvance(e.target.checked)}
                  className="w-4 h-4 accent-[color:var(--ink)]"
                  data-testid="auto-advance-toggle"
                />
                <span className="text-sm flex items-center gap-2">
                  <FastForward size={14}/> Auto-advance on match (for hands-free)
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Chunk-size selector (script mode) */}
        {mode === "script" && (
          <div className="mt-10">
            <label className="eyebrow block mb-3">Chunk size</label>
            <div className="grid sm:grid-cols-3 gap-px bg-[color:var(--rule)] border rule" data-testid="chunk-size-switch">
              {CHUNK_SIZES.map((c) => {
                const selected = chunkSize === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setChunkSize(c.key)}
                    className={`relative p-5 text-left transition-colors ${selected ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`chunk-${c.key}`}
                    aria-pressed={selected}
                  >
                    {selected && <CheckCircle size={18} weight="fill" className="absolute top-3 right-3"/>}
                    <div className="font-mono text-sm uppercase tracking-widest">{c.label}</div>
                    <div className={`text-xs mt-1 ${selected ? "text-white/70" : "text-[color:var(--ink-muted)]"}`}>{c.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content area */}
        {mode === "script" ? (
          <div className="mt-10 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="eyebrow">Your text</label>
                <button
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="text-xs flex items-center gap-1 link-underline"
                  data-testid="btn-upload"
                >
                  <UploadSimple size={14}/> {uploading ? "Importing…" : "Import PDF / Word / TXT"}
                </button>
                <input ref={fileInput} type="file" accept=".pdf,.docx,.txt" onChange={onFile} className="hidden"/>
              </div>
              <textarea
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                rows={14}
                placeholder="Paste your script, notes, or presentation — or import a PDF / Word doc…"
                className="w-full bg-white border rule px-4 py-3 font-serif text-lg leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="input-source-text"
              />
              <button
                onClick={doChunk}
                disabled={chunking}
                className="btn-primary mt-4"
                data-testid="btn-chunk"
              >
                <Sparkle size={16} weight="fill"/> {chunking ? "Chunking…" : "Chunk with AI"}
              </button>
              {chunking && (
                <AIProgressIndicator
                  title="Chunking"
                  message={<>AI is splitting your text into <span className="font-mono">{chunkSize}</span> chunks. Hold tight — usually takes a few seconds.</>}
                  testId="chunking-progress"
                />
              )}
            </div>
            <div className="md:col-span-3">
              <div className="flex items-center justify-between mb-2">
                <label className="eyebrow">Chunks · {chunks.length}</label>
                {chunks.length > 0 && (
                  <button onClick={addChunk} className="text-xs flex items-center gap-1 link-underline">
                    <Plus size={12}/> Add chunk
                  </button>
                )}
              </div>
              <div className="border rule bg-white divide-y rule max-h-[520px] overflow-auto">
                {chunks.length === 0 ? (
                  <div className="p-10 text-center text-sm text-[color:var(--ink-muted)]">
                    Your chunks appear here. Paste or import text, then <span className="font-mono">Chunk with AI</span>.
                  </div>
                ) : (
                  chunks.map((c, i) => (
                    <div key={i} className="p-5 flex gap-4 items-start" data-testid={`chunk-row-${i}`}>
                      <div className="font-mono text-xs pt-2 w-8">{String(i+1).padStart(2,"0")}</div>
                      <textarea
                        value={c}
                        onChange={(e) => updateChunk(i, e.target.value)}
                        rows={2}
                        className="flex-1 bg-transparent border rule px-3 py-2 font-serif leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                      />
                      <button onClick={() => removeChunk(i)} className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] mt-2">
                        <Trash size={16}/>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Q&A style + source switches */}
            <div className="mt-10 grid md:grid-cols-2 gap-6">
              <div>
                <label className="eyebrow block mb-3">Card style</label>
                <div className="grid grid-cols-2 gap-px bg-[color:var(--rule)] border rule" data-testid="qa-style-switch">
                  <button
                    onClick={() => setQaStyle("statement")}
                    className={`bg-white p-4 text-left ${qaStyle === "statement" ? "" : "opacity-60 hover:opacity-100"}`}
                    data-testid="qa-style-statement"
                  >
                    <div className="font-mono text-xs uppercase tracking-widest">Statement</div>
                    <div className="text-xs text-[color:var(--ink-muted)] mt-1">App reads &ldquo;The sky is blue&rdquo; — you repeat it.</div>
                  </button>
                  <button
                    onClick={() => setQaStyle("flashcard")}
                    className={`bg-white p-4 text-left ${qaStyle === "flashcard" ? "" : "opacity-60 hover:opacity-100"}`}
                    data-testid="qa-style-flashcard"
                  >
                    <div className="font-mono text-xs uppercase tracking-widest">Flashcard</div>
                    <div className="text-xs text-[color:var(--ink-muted)] mt-1">App asks &ldquo;What color is the sky?&rdquo; — you answer.</div>
                  </button>
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-3">Source</label>
                <div className="grid grid-cols-2 gap-px bg-[color:var(--rule)] border rule">
                  <button
                    onClick={() => setQaSource("paste")}
                    className={`bg-white p-4 text-left ${qaSource === "paste" ? "" : "opacity-60 hover:opacity-100"}`}
                    data-testid="qa-source-paste"
                  >
                    <div className="font-mono text-xs uppercase tracking-widest">Paste MCQs</div>
                    <div className="text-xs text-[color:var(--ink-muted)] mt-1">You already have questions? Drop them in.</div>
                  </button>
                  <button
                    onClick={() => setQaSource("generate")}
                    className={`bg-white p-4 text-left ${qaSource === "generate" ? "" : "opacity-60 hover:opacity-100"}`}
                    data-testid="qa-source-generate"
                  >
                    <div className="font-mono text-xs uppercase tracking-widest">Generate from topic</div>
                    <div className="text-xs text-[color:var(--ink-muted)] mt-1">AI creates flashcards from a subject or notes.</div>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 grid md:grid-cols-5 gap-10">
              <div className="md:col-span-2">
                {qaSource === "paste" ? (
                  <>
                    <label className="eyebrow block mb-2">Paste your questions</label>
                    <textarea
                      value={qaInput}
                      onChange={(e) => setQaInput(e.target.value)}
                      rows={14}
                      placeholder={`1. What color is the sky?\n  a) blue\n  b) purple\n  c) dinosaur\n\n2. The capital of France is…\n  a) Madrid\n  b) Paris\n  c) Berlin`}
                      className="w-full bg-white border rule px-4 py-3 font-mono text-sm leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                      data-testid="input-qa-text"
                    />
                    <button
                      onClick={doConvertQA}
                      disabled={converting}
                      className="btn-primary mt-4"
                      data-testid="btn-convert-qa"
                    >
                      <Sparkle size={16} weight="fill"/> {converting ? "Converting…" : `Convert to ${qaStyle === "flashcard" ? "flashcards" : "statements"}`}
                    </button>
                    <p className="text-xs text-[color:var(--ink-muted)] mt-3">
                      Numbered or blank-line-separated. We split automatically.
                    </p>
                    {converting && (
                      <AIProgressIndicator
                        title="Converting"
                        message={<>AI is turning your text into <span className="font-mono">{qaStyle === "flashcard" ? "flashcards" : "statement cards"}</span>. Hold tight — usually takes a few seconds.</>}
                        testId="converting-progress"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <label className="eyebrow block mb-2">Topic or notes</label>
                    <textarea
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      rows={12}
                      placeholder={`e.g. ITIL 4 — service value chain\nOr paste chapter notes here; AI will turn the key facts into flashcards.`}
                      className="w-full bg-white border rule px-4 py-3 font-serif text-base leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                      data-testid="input-topic"
                    />
                    <div className="mt-3 flex items-center gap-3">
                      <label className="eyebrow">How many</label>
                      <select
                        value={genCount}
                        onChange={(e) => setGenCount(Number(e.target.value))}
                        className="bg-white border rule px-3 py-2 text-sm"
                        data-testid="input-gen-count"
                      >
                        {[5, 10, 15, 20, 30].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <button
                      onClick={doGenerate}
                      disabled={generating}
                      className="btn-primary mt-4"
                      data-testid="btn-generate-qa"
                    >
                      <Sparkle size={16} weight="fill"/> {generating ? "Generating…" : "Generate flashcards"}
                    </button>
                    <p className="text-xs text-[color:var(--ink-muted)] mt-3">
                      Uses AI to draft clear question/answer pairs from your input. Review before saving.
                    </p>
                    {generating && (
                      <AIProgressIndicator
                        title="Generating"
                        message={<>AI is drafting <span className="font-mono">{genCount}</span> flashcards from your topic. This can take up to 30 seconds for longer inputs.</>}
                        testId="generating-progress"
                      />
                    )}
                  </>
                )}
              </div>
              <div className="md:col-span-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="eyebrow">{qaStyle === "flashcard" ? "Flashcards" : "Statement cards"} · {qaItems.length}</label>
                  {qaItems.length > 0 && (
                    <button onClick={addQA} className="text-xs flex items-center gap-1 link-underline">
                      <Plus size={12}/> Add card
                    </button>
                  )}
                </div>
                <div className="border rule bg-white divide-y rule max-h-[560px] overflow-auto">
                  {qaItems.length === 0 ? (
                    <div className="p-10 text-center text-sm text-[color:var(--ink-muted)]">
                      Your cards appear here after conversion or generation.
                    </div>
                  ) : qaStyle === "flashcard" ? (
                    qaItems.map((q, i) => (
                      <div key={i} className="p-5" data-testid={`qa-row-${i}`}>
                        <div className="flex items-start gap-4">
                          <div className="font-mono text-xs pt-2 w-8">{String(i+1).padStart(2,"0")}</div>
                          <div className="flex-1 space-y-2">
                            <textarea
                              value={q.prompt || ""}
                              onChange={(e) => updateQA(i, "prompt", e.target.value)}
                              rows={2}
                              placeholder="Question (what gets read aloud)…"
                              className="w-full bg-transparent border rule px-3 py-2 font-serif text-base leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                            />
                            <textarea
                              value={q.answer || ""}
                              onChange={(e) => updateQA(i, "answer", e.target.value)}
                              rows={2}
                              placeholder="Short answer (what you&apos;ll say)…"
                              className="w-full bg-transparent border-l-2 border-[color:var(--oxford)] rule px-3 py-2 font-serif text-base leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                            />
                            <input
                              value={q.alt_answers_text || ""}
                              onChange={(e) => updateQA(i, "alt_answers_text", e.target.value)}
                              placeholder="Alternate accepted answers (separate with | )  e.g. Windows 10 | Windows 11 | Win10"
                              className="w-full bg-transparent border rule px-3 py-1.5 text-xs font-mono text-[color:var(--ink-muted)] focus:outline-none focus:border-[color:var(--ink)]"
                            />
                          </div>
                          <button onClick={() => removeQA(i)} className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] mt-2">
                            <Trash size={16}/>
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    qaItems.map((q, i) => (
                      <div key={i} className="p-5" data-testid={`qa-row-${i}`}>
                        <div className="flex items-start gap-4">
                          <div className="font-mono text-xs pt-2 w-8">{String(i+1).padStart(2,"0")}</div>
                          <div className="flex-1">
                            {q.original && (
                              <div className="text-xs text-[color:var(--ink-muted)] mb-2 whitespace-pre-wrap font-mono">{q.original}</div>
                            )}
                            <textarea
                              value={q.statement || ""}
                              onChange={(e) => updateQA(i, "statement", e.target.value)}
                              rows={2}
                              placeholder="Statement…"
                              className="w-full bg-transparent border rule px-3 py-2 font-serif text-lg leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                            />
                          </div>
                          <button onClick={() => removeQA(i)} className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] mt-2">
                            <Trash size={16}/>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="sticky bottom-0 bg-[color:var(--paper)]/95 backdrop-blur-xl border-t rule mt-12 -mx-6 md:-mx-10 px-6 md:px-10 py-5 flex justify-end gap-4">
          <Link to="/dashboard" className="btn-ghost" data-testid="btn-cancel">Cancel</Link>
          <button
            disabled={!canSave || saving}
            onClick={save}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="btn-save-deck"
          >
            <FloppyDisk size={16} weight="fill"/> {saving ? "Saving…" : "Save deck"} <ArrowRight size={16} weight="bold"/>
          </button>
        </div>
      </main>
    </div>
  );
}
